import React, { useState, useEffect } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { validateImage, convertToWebP } from '../../utils/imageUtils';
import { GalleryImage } from '../../types/gallery';

interface ImageSelectorProps {
  images: string[];
  onChange: (urls: string[]) => void;
  maxImages: number;
  type: 'carousel' | 'gallery';
}

const ImageSelector: React.FC<ImageSelectorProps> = ({ images, onChange, maxImages, type }) => {
  const [uploading, setUploading] = useState(false);
  const [isGalleryModalOpen, setIsGalleryModalOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([]);

  useEffect(() => {
    fetchGalleryImages();
  }, []);

  const fetchGalleryImages = async () => {
    try {
      const { data, error } = await supabase
        .from('gallery_images')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setGalleryImages(data || []);
    } catch (err) {
      console.error('Error fetching gallery images:', err);
    }
  };

  const uploadImage = async (file: File) => {
    try {
      setUploading(true);

      const validationError = validateImage(file);
      if (validationError) {
        alert(validationError);
        return null;
      }

      let convertedFile: File;
      try {
        convertedFile = await convertToWebP(file);
      } catch (convErr) {
        alert(convErr instanceof Error ? convErr.message : 'Failed to process image. Try a different file.');
        return null;
      }

      const ext = convertedFile.type === 'image/webp' ? 'webp' : 'jpg';
      const fileName = `${Math.random()}.${ext}`;
      const filePath = `tour-images/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('tours')
        .upload(filePath, convertedFile, {
          contentType: convertedFile.type,
          upsert: false,
        });

      if (uploadError) {
        alert(`Upload failed: ${uploadError.message}`);
        return null;
      }

      const { data } = supabase.storage
        .from('tours')
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const remainingSlots = maxImages - images.length;
    const filesToUpload = Array.from(e.target.files).slice(0, remainingSlots);

    const uploadPromises = filesToUpload.map(uploadImage);
    const urls = await Promise.all(uploadPromises);
    const validUrls = urls.filter((url): url is string => url !== null);

    onChange([...images, ...validUrls]);
  };

  const removeImage = (index: number) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    onChange(newImages);
  };

  const handleGallerySelect = (image: GalleryImage) => {
    if (images.length < maxImages) {
      onChange([...images, image.image_url]);
    }
    setIsGalleryModalOpen(false);
  };

  return (
    <div className="space-y-4">
      {/* Image Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.map((url, index) => (
          <div key={index} className="relative aspect-video group">
            <img
              src={url}
              alt={`Tour ${type} image ${index + 1}`}
              className="w-full h-full object-cover rounded-lg"
            />
            <button
              type="button"
              onClick={() => removeImage(index)}
              className="absolute top-2 right-2 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        ))}

        {images.length < maxImages && (
          <div className="grid grid-cols-2 gap-2">
            <label className="relative aspect-video cursor-pointer">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                disabled={uploading}
                className="hidden"
              />
              <div className="absolute inset-0 border-2 border-dashed border-white/20 rounded-lg flex items-center justify-center hover:border-white/40 transition-colors">
                <div className="text-center">
                  <Upload className="w-6 h-6 mx-auto mb-1 text-white/40" />
                  <span className="text-xs text-white/40">
                    {uploading ? 'Uploading...' : 'Upload'}
                  </span>
                </div>
              </div>
            </label>

            <button
              type="button"
              onClick={() => setIsGalleryModalOpen(true)}
              className="relative aspect-video"
            >
              <div className="absolute inset-0 border-2 border-dashed border-white/20 rounded-lg flex items-center justify-center hover:border-white/40 transition-colors">
                <div className="text-center">
                  <ImageIcon className="w-6 h-6 mx-auto mb-1 text-white/40" />
                  <span className="text-xs text-white/40">Gallery</span>
                </div>
              </div>
            </button>
          </div>
        )}
      </div>

      <p className="text-sm text-white/50">
        {images.length} of {maxImages} images used
      </p>

      {/* Gallery Selection Modal */}
      {isGalleryModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-[#1F2B1F] rounded-xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b border-white/10">
              <h3 className="text-xl font-semibold">Select from Gallery</h3>
              <button
                type="button"
                onClick={() => setIsGalleryModalOpen(false)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {galleryImages.map((image) => (
                  <button
                    key={image.id}
                    type="button"
                    onClick={() => handleGallerySelect(image)}
                    className="relative aspect-video group"
                  >
                    <img
                      src={image.image_url}
                      alt={image.title}
                      className="w-full h-full object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-sm">Select</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageSelector;